import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { analizarFactura, createFactura } from '../api/Facturas/facturas'
import { getPermissions } from '../components/Common/Permissions'
import { useAuth } from './useAuth'

const ESTADO_INICIAL = {
  abierto: false,
  cargando: false,
  guardando: false,
  urlArchivo: null,
  tipoCarga: null,
  documentoId: null,
  diferido: false,
  datos: null,
  error: null,
  aseguradoraNombre: '',
  clienteNombre: '',
  analisis: null
}

/**
 * Encapsula el ciclo "archivo ya subido -> analizar con IA -> modal editable -> confirmar".
 *
 * Cuando el documento de origen todavia no existe (por ejemplo una facturacion Lizen
 * que se crea recien al guardar el formulario), se usa `diferido`: la factura confirmada
 * queda pendiente y la pagina la persiste con `guardarPendientes(documentoId)` una vez
 * que conoce el id.
 *
 * Si se suben varios archivos de una, se analizan de a uno: al cerrar el modal de un
 * archivo se abre el del siguiente.
 *
 * Solo se analiza si el usuario tiene el permiso `analizar_facturas` (operarios);
 * sin ese permiso la carga del archivo sigue su curso normal sin abrir el modal.
 */
export function useAnalisisFactura() {
  const { auth } = useAuth()
  const puedeAnalizar = getPermissions('analizar_facturas', auth)
  const [estado, setEstado] = useState(ESTADO_INICIAL)
  const [pendientes, setPendientes] = useState([])
  const cola = useRef([])

  const analizarSiguiente = useCallback(async () => {
    const siguiente = cola.current.shift()

    if (!siguiente) {
      setEstado(ESTADO_INICIAL)
      return
    }

    const { urlArchivo, tipoCarga, documentoId, diferido } = siguiente

    setEstado({
      ...ESTADO_INICIAL,
      abierto: true,
      cargando: true,
      urlArchivo,
      tipoCarga,
      documentoId,
      diferido
    })

    try {
      const respuesta = await analizarFactura({ urlArchivo, tipoCarga, documentoId })

      setEstado((previo) => ({
        ...previo,
        cargando: false,
        datos: respuesta.datos,
        error: respuesta.estado_analisis === 'error' ? respuesta.analisis_error : null,
        aseguradoraNombre: respuesta.aseguradora_nombre,
        clienteNombre: respuesta.cliente_nombre,
        analisis: respuesta
      }))
    } catch (error) {
      setEstado((previo) => ({
        ...previo,
        cargando: false,
        datos: null,
        error: error.message || 'No se pudo analizar la factura.'
      }))
    }
  }, [])

  /** Encola uno o varios archivos y arranca por el primero. */
  const analizarArchivos = useCallback((archivos) => {
    if (!puedeAnalizar) return

    const items = (Array.isArray(archivos) ? archivos : [archivos]).filter(
      (item) => item && item.urlArchivo
    )
    if (items.length === 0) return

    cola.current = cola.current.concat(items)
    analizarSiguiente()
  }, [puedeAnalizar, analizarSiguiente])

  const analizarArchivo = useCallback((archivo) => analizarArchivos([archivo]), [analizarArchivos])

  const cerrar = useCallback(() => {
    analizarSiguiente()
  }, [analizarSiguiente])

  const descartarTodo = useCallback(() => {
    cola.current = []
    setEstado(ESTADO_INICIAL)
  }, [])

  const confirmar = useCallback(async (valores) => {
    const { urlArchivo, tipoCarga, documentoId, diferido, analisis } = estado

    const request = {
      ...valores,
      url_archivo: urlArchivo,
      tipo_carga: tipoCarga,
      documento_id: documentoId,
      estado_analisis: analisis?.estado_analisis || 'manual',
      analisis_error: analisis?.analisis_error || '',
      analisis_raw: analisis?.analisis_raw || null,
      modelo_ia: analisis?.modelo_ia || '',
      analizado_at: analisis?.analizado_at || null
    }

    if (diferido) {
      setPendientes((previos) => [...previos, request])
      analizarSiguiente()
      return { pendiente: true }
    }

    setEstado((previo) => ({ ...previo, guardando: true }))

    try {
      const factura = await createFactura(request)
      toast.success('Factura registrada.')
      analizarSiguiente()
      return { pendiente: false, factura }
    } catch (error) {
      setEstado((previo) => ({ ...previo, guardando: false }))
      toast.error('No se pudo registrar la factura.')
      throw error
    }
  }, [estado, analizarSiguiente])

  /**
   * Persiste las facturas confirmadas en modo diferido, ya con el id del documento de origen.
   * Acepta un id fijo o una funcion, para los casos donde cada archivo genera su propio
   * documento (por ejemplo los archivos de facturacion de Orion).
   */
  const guardarPendientes = useCallback(async (documentoIdOResolver) => {
    if (pendientes.length === 0) return

    const resolverId = typeof documentoIdOResolver === 'function'
      ? documentoIdOResolver
      : () => documentoIdOResolver

    try {
      await Promise.all(
        pendientes.map((request) => createFactura({ ...request, documento_id: resolverId(request) || null }))
      )
    } catch (error) {
      toast.error('Se guardó el documento, pero no se pudo registrar la factura en el apartado de Facturas.')
    } finally {
      setPendientes([])
    }
  }, [pendientes])

  const limpiarPendientes = useCallback(() => setPendientes([]), [])

  return {
    puedeAnalizar,
    modalProps: {
      open: estado.abierto,
      cargando: estado.cargando,
      guardando: estado.guardando,
      urlArchivo: estado.urlArchivo,
      datos: estado.datos,
      error: estado.error,
      aseguradoraNombre: estado.aseguradoraNombre,
      clienteNombre: estado.clienteNombre,
      onConfirm: confirmar,
      onClose: cerrar
    },
    analizarArchivo,
    analizarArchivos,
    descartarTodo,
    pendientes,
    guardarPendientes,
    limpiarPendientes
  }
}
